import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../styles/DeleteAccount.css';

// Remove trailing slash to avoid double slashes in URLs
const API_URL = (process.env.REACT_APP_API_URL || 'https://camino-backend-iooi.onrender.com').replace(/\/$/, '');

const DeleteAccount = () => {
  const { isAuthenticated, getAccessTokenSilently, isLoading, loginWithRedirect, logout } = useAuth0();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tokenValidationStatus, setTokenValidationStatus] = useState('checking'); // 'checking', 'valid', 'invalid', 'none'
  const [tokenUserInfo, setTokenUserInfo] = useState(null);
  
  const location = useLocation();
  const navigate = useNavigate();

  // Check for deletion token in URL on mount
  useEffect(() => {
    const validateToken = async () => {
      const params = new URLSearchParams(location.search);
      const deleteToken = params.get('deleteToken');
      
      if (!deleteToken) {
        setTokenValidationStatus('none');
        return;
      }

      console.log('🔑 Deletion token found in URL, validating...');
      
      try {
        const res = await fetch(`${API_URL}/api/users/validate-deletion-token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token: deleteToken }),
        });

        const data = await res.json();

        if (data.valid) {
          console.log('✅ Token validated successfully');
          setTokenValidationStatus('valid');
          setTokenUserInfo(data);
          
          // Clear token from URL for security
          navigate('/delete-account', { replace: true });
          
        } else {
          console.error('❌ Token validation failed:', data.error);
          setTokenValidationStatus('invalid');
          
          // Show specific error message
          if (data.error === 'TOKEN_EXPIRED') {
            setStatus('token_expired');
          } else if (data.error === 'TOKEN_USED') {
            setStatus('token_used');
          } else {
            setStatus('token_invalid');
          }
        }
      } catch (err) {
        console.error('❌ Error validating token:', err);
        setTokenValidationStatus('invalid');
        setStatus('token_error');
      }
    };

    validateToken();
  }, [location.search, navigate]);

  const handleDelete = async () => {
    setLoading(true);
    setStatus(null);
    try {
      let token;
      
      // If we have a valid token from mobile app, use it
      // Otherwise, get token from Auth0 (regular web login)
      if (tokenUserInfo && tokenValidationStatus === 'valid') {
        // For token-based access, we still need an Auth0 token to call the delete endpoint
        // Prompt user to log in if not already authenticated
        if (!isAuthenticated) {
          await loginWithRedirect({
            appState: { returnTo: '/delete-account' },
          });
          return;
        }
        token = await getAccessTokenSilently();
      } else {
        token = await getAccessTokenSilently();
      }
      
      const res = await fetch(`${API_URL}/api/users/delete-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || 'Hiba történt.');
      }
      setStatus('success');
      
      // Log out the user after successful account deletion
      // Wait a brief moment to show the success message, then logout
      setTimeout(() => {
        logout({
          logoutParams: {
            returnTo: window.location.origin
          }
        });
      }, 2000);
    } catch (err) {
      console.error('Delete account error:', err);
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking auth or token
  if (isLoading || tokenValidationStatus === 'checking') {
    return <div className="loading">Betöltés...</div>;
  }

  // Handle token validation errors
  if (status === 'token_expired') {
    return (
      <div className="delete-account-page">
        <div className="delete-account-container">
          <div className="delete-account-error-card">
            <h1 className="delete-account-error-title">A link lejárt</h1>
            <p className="delete-account-error-description">
              Ez a törlési link már lejárt. Kérlek, kérd újra a fiókod törlését a mobilalkalmazásból.
            </p>
            <p className="delete-account-error-note">
              A biztonsági okokból a törlési linkek csak 5 percig érvényesek.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'token_used') {
    return (
      <div className="delete-account-page">
        <div className="delete-account-container">
          <div className="delete-account-error-card">
            <h1 className="delete-account-error-title">A link már használva volt</h1>
            <p className="delete-account-error-description">
              Ez a törlési link már használva volt. Ha még mindig törölni szeretnéd a fiókodat, kérj új linket a mobilalkalmazásból.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'token_invalid' || status === 'token_error') {
    return (
      <div className="delete-account-page">
        <div className="delete-account-container">
          <div className="delete-account-error-card">
            <h1 className="delete-account-error-title">Érvénytelen link</h1>
            <p className="delete-account-error-description">
              Ez a törlési link érvénytelen vagy hibás. Kérlek, kérd újra a fiókod törlését a mobilalkalmazásból.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Check if user is authenticated (either via token or regular login)
  const isUserAuthenticated = isAuthenticated || (tokenValidationStatus === 'valid' && tokenUserInfo);

  if (!isUserAuthenticated) {
    return <div className="api-error">Ehhez az oldalhoz be kell jelentkezni.</div>;
  }

  // Show user info if available (from token or Auth0)
  const displayName = tokenUserInfo?.name || 'Felhasználó';

  return (
    <div className="delete-account-page">
      <div className="delete-account-container">
        <div className="delete-account-card">
          {tokenValidationStatus === 'valid' && (
            <div className="delete-account-success-badge">
              <p>Sikeres hitelesítés mobilalkalmazásból</p>
            </div>
          )}
          
          <h1 className="delete-account-title">Fiók törlése</h1>
          <p className="delete-account-description">
            Ezen az oldalon törölheted a fiókodat. A törlés azonnal megtörténik és visszavonhatatlan!
          </p>
          
          <div className="delete-account-warning">
            <ul>
              <li>Az összes személyes adatod azonnal törlésre kerül.</li>
              <li>A törlés végleges és visszavonhatatlan.</li>
              <li>Automatikusan ki leszel jelentkeztetve.</li>
            </ul>
          </div>
          
          {status === 'success' ? (
            <div className="delete-account-success-message">
              <p>A fiókod sikeresen törölve lett. Kijelentkeztetünk...</p>
            </div>
          ) : status === 'error' ? (
            <div className="delete-account-error-message">
              <p>Hiba történt a törlési kérelem során. Próbáld újra később.</p>
            </div>
          ) : null}
          
          {status !== 'success' && (
            <button
              className="delete-account-button"
              onClick={handleDelete}
              disabled={loading}
            >
              {loading ? 'Törlés folyamatban...' : 'Fiók azonnali törlése'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeleteAccount; 